import { splitWhere } from '../core/group';
import { formatClock } from '../core/format';
import { timeToX, xToTime, type Marker, type Span } from './markers';

export interface SpeedPoint {
  readonly time: number;
  readonly min: number;
  readonly max: number;
}

export interface ScrubberModel {
  readonly span: Span;
  readonly utcOffset: number;
  readonly coverage: readonly Span[];
  readonly speed: readonly SpeedPoint[];
  readonly brake: readonly Span[];
  readonly markers: readonly Marker[];
}

export type ScrubPhase = 'start' | 'move' | 'end';

const HEIGHT = 64;
const COVERAGE_HEIGHT = 6;
const MARKER_ZONE = 14;
const HIT_RADIUS_PX = 6;
const MIN_SPEED_SCALE = 30;
const SPEED_BREAK_MS = 1000;

interface Palette {
  readonly track: string;
  readonly coverage: string;
  readonly speed: string;
  readonly speedFill: string;
  readonly brake: string;
  readonly playhead: string;
  readonly markers: Readonly<Record<Marker['kind'], string>>;
}

function readPalette(element: HTMLElement): Palette {
  const style = getComputedStyle(element);
  const value = (name: string): string => style.getPropertyValue(name).trim();
  return {
    track: value('--tv-scrub-track'),
    coverage: value('--tv-scrub-coverage'),
    speed: value('--tv-scrub-speed'),
    speedFill: value('--tv-scrub-speed-fill'),
    brake: value('--tv-scrub-brake'),
    playhead: value('--tv-scrub-playhead'),
    markers: {
      trigger: value('--tv-marker-trigger'),
      incident: value('--tv-marker-incident'),
      brake: value('--tv-marker-brake'),
      launch: value('--tv-marker-launch'),
      lateral: value('--tv-marker-lateral'),
      mode: value('--tv-marker-mode'),
      nearby: value('--tv-marker-nearby'),
      change: value('--tv-marker-change'),
    },
  };
}

export class Scrubber {
  private model: ScrubberModel | undefined;
  private speedRuns: readonly (readonly SpeedPoint[])[] = [];
  private speedScale = MIN_SPEED_SCALE;
  private time = 0;
  private palette: Palette;
  private dragging = false;
  private readonly resize = new ResizeObserver(() => this.draw());

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly tip: HTMLElement,
    private readonly onScrub: (time: number, phase: ScrubPhase) => void,
  ) {
    this.palette = readPalette(canvas);
    this.resize.observe(canvas);
    canvas.addEventListener('pointerdown', this.press);
    canvas.addEventListener('pointermove', this.move);
    canvas.addEventListener('pointerup', this.release);
    canvas.addEventListener('pointercancel', this.release);
    canvas.addEventListener('pointerleave', () => (this.tip.hidden = true));
  }

  setModel(model: ScrubberModel): void {
    this.model = model;
    this.speedRuns = splitWhere(model.speed, (previous, point) => point.time - previous.time > SPEED_BREAK_MS);
    this.speedScale = model.speed.reduce((scale, point) => Math.max(scale, point.max), MIN_SPEED_SCALE);
    this.palette = readPalette(this.canvas);
    this.draw();
  }

  setTime(time: number): void {
    if (Math.abs(time - this.time) < 1) return;
    this.time = time;
    const span = this.model?.span;
    if (span) this.canvas.setAttribute('aria-valuenow', String(Math.round(((time - span.start) / Math.max(1, span.end - span.start)) * 100)));
    this.draw();
  }

  destroy(): void {
    this.resize.disconnect();
    this.canvas.removeEventListener('pointerdown', this.press);
    this.canvas.removeEventListener('pointermove', this.move);
    this.canvas.removeEventListener('pointerup', this.release);
    this.canvas.removeEventListener('pointercancel', this.release);
  }

  private timeAt(event: PointerEvent): number | undefined {
    const span = this.model?.span;
    if (!span) return undefined;
    const box = this.canvas.getBoundingClientRect();
    return xToTime(event.clientX - box.left, span, box.width);
  }

  private readonly press = (event: PointerEvent): void => {
    const time = this.timeAt(event);
    if (time === undefined) return;
    this.dragging = true;
    this.canvas.setPointerCapture(event.pointerId);
    this.onScrub(this.snap(event, time), 'start');
  };

  private readonly move = (event: PointerEvent): void => {
    const time = this.timeAt(event);
    if (time === undefined) return;
    this.showTip(event, time);
    if (this.dragging) this.onScrub(time, 'move');
  };

  private readonly release = (event: PointerEvent): void => {
    if (!this.dragging) return;
    this.dragging = false;
    const time = this.timeAt(event);
    if (time !== undefined) this.onScrub(time, 'end');
  };

  private nearestMarker(event: PointerEvent): Marker | undefined {
    const model = this.model;
    if (!model) return undefined;
    const box = this.canvas.getBoundingClientRect();
    const x = event.clientX - box.left;
    return model.markers.find((marker) => Math.abs(timeToX(marker.time, model.span, box.width) - x) <= HIT_RADIUS_PX);
  }

  private snap(event: PointerEvent, time: number): number {
    return this.nearestMarker(event)?.time ?? time;
  }

  private showTip(event: PointerEvent, time: number): void {
    const model = this.model;
    if (!model) return;
    const marker = this.nearestMarker(event);
    const box = this.canvas.getBoundingClientRect();
    this.tip.textContent = marker ? `${marker.label} · ${formatClock(marker.time + model.utcOffset)}` : formatClock(time + model.utcOffset);
    this.tip.style.left = `${Math.min(box.width - 4, Math.max(4, event.clientX - box.left))}px`;
    this.tip.hidden = false;
  }

  private draw(): void {
    const model = this.model;
    const context = this.canvas.getContext('2d');
    if (!model || !context) return;
    const ratio = window.devicePixelRatio || 1;
    const width = this.canvas.clientWidth;
    if (this.canvas.width !== Math.round(width * ratio)) this.canvas.width = Math.round(width * ratio);
    if (this.canvas.height !== Math.round(HEIGHT * ratio)) this.canvas.height = Math.round(HEIGHT * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, width, HEIGHT);
    const x = (time: number): number => timeToX(time, model.span, width);
    this.drawCoverage(context, model, x, width);
    this.drawSpeed(context, x);
    this.drawMarkers(context, model, x);
    context.fillStyle = this.palette.playhead;
    context.fillRect(Math.round(x(this.time)) - 1, 0, 2, HEIGHT);
  }

  private drawCoverage(context: CanvasRenderingContext2D, model: ScrubberModel, x: (time: number) => number, width: number): void {
    context.fillStyle = this.palette.track;
    context.fillRect(0, HEIGHT - COVERAGE_HEIGHT, width, COVERAGE_HEIGHT);
    context.fillStyle = this.palette.coverage;
    for (const span of model.coverage) context.fillRect(x(span.start), HEIGHT - COVERAGE_HEIGHT, Math.max(1, x(span.end) - x(span.start)), COVERAGE_HEIGHT);
    context.fillStyle = this.palette.brake;
    for (const span of model.brake) context.fillRect(x(span.start), MARKER_ZONE, Math.max(1, x(span.end) - x(span.start)), HEIGHT - MARKER_ZONE - COVERAGE_HEIGHT - 2);
  }

  private drawSpeed(context: CanvasRenderingContext2D, x: (time: number) => number): void {
    const top = MARKER_ZONE + 2;
    const bottom = HEIGHT - COVERAGE_HEIGHT - 4;
    const y = (speed: number): number => bottom - (speed / this.speedScale) * (bottom - top);
    context.strokeStyle = this.palette.speed;
    context.fillStyle = this.palette.speedFill;
    context.lineWidth = 1.25;
    for (const run of this.speedRuns) {
      if (run.length < 2) continue;
      context.beginPath();
      run.forEach((point, i) => (i === 0 ? context.moveTo(x(point.time), y(point.max)) : context.lineTo(x(point.time), y(point.max))));
      context.stroke();
      context.lineTo(x(run.at(-1)!.time), bottom);
      context.lineTo(x(run[0]!.time), bottom);
      context.closePath();
      context.fill();
    }
  }

  private drawMarkers(context: CanvasRenderingContext2D, model: ScrubberModel, x: (time: number) => number): void {
    for (const marker of model.markers) {
      const left = Math.round(x(marker.time));
      context.fillStyle = this.palette.markers[marker.kind];
      const width = marker.kind === 'trigger' || marker.kind === 'incident' ? 2 : 1;
      context.fillRect(left - width / 2, 0, width, HEIGHT - COVERAGE_HEIGHT);
      context.beginPath();
      context.moveTo(left - 5, 0);
      context.lineTo(left + 5, 0);
      context.lineTo(left, 7);
      context.closePath();
      context.fill();
    }
  }
}
